import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FIELDS as FF } from "@/lib/fields";
import { Info } from "@/components/Info";
import { Pencil, Check, X } from "lucide-react";

// Item C-K: Next Action combo — stage options (Admin-editable) + my last 5 custom entries + type your own.
export function NextActionField({ value, stage, editable, onSave, testid = "next-action-field", lastConvIsToday = false }) {
  const [editing, setEditing] = useState(false);
  const [opts, setOpts] = useState({ options: [], recent: [] });
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [spoke, setSpoke] = useState(true);       // 22 Oct: default ON — changing the next step usually follows a conversation
  useEffect(() => { if (editing) api.get("/meta/next-actions", { params: { stage } }).then((r) => setOpts(r.data)).catch(() => {}); }, [editing, stage]);
  const save = async (v) => {
    if (v === (value || "")) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(FF.NEXT_ACTION, v || null, { logConversation: spoke && !lastConvIsToday });
    setSaving(false);
    if (ok !== false) { setEditing(false); setCustom(false); }
  };
  const label = <div className="text-[11px] font-medium uppercase text-[#6B7280] flex items-center gap-1" style={{ letterSpacing: "0.06em" }}>Next Action <Info k="next_action" /></div>;
  if (!editing) {
    return (
      <div className="group">{label}
        <div className="flex items-start justify-between gap-2 mt-1.5">
          <div className="text-sm text-[#333333]" data-testid={testid}>{value || <span className="text-[#C5C4C4]">—</span>}</div>
          {editable && <button onClick={() => { setDraft(value || ""); setEditing(true); }} data-testid={`${testid}-edit`} className="opacity-0 group-hover:opacity-100 text-[#C5C4C4] hover:text-[#FF4802]"><Pencil className="w-3.5 h-3.5" /></button>}
        </div>
      </div>
    );
  }
  return (
    <div>{label}
      <div className="mt-1.5 space-y-1.5" data-testid={`${testid}-editor`}>
        {!custom ? (
          <select autoFocus value="" onChange={(e) => { const v = e.target.value; if (v === "__custom") { setCustom(true); } else if (v) save(v); }} data-testid={`${testid}-select`} className="w-full border border-[#E2E2E2] rounded-lg px-2 py-1.5 text-sm bg-white focus:border-[#FF4802] outline-none">
            <option value="">Pick a next action…</option>
            {opts.options.length > 0 && <optgroup label={`${stage || "Stage"} options`}>{opts.options.map((o) => <option key={o} value={o}>{o}</option>)}</optgroup>}
            {opts.recent.length > 0 && <optgroup label="My recent">{opts.recent.map((o) => <option key={`r-${o}`} value={o}>{o}</option>)}</optgroup>}
            <option value="__custom">Type your own…</option>
          </select>
        ) : (
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(draft.trim()); if (e.key === "Escape") setEditing(false); }} data-testid={`${testid}-input`} placeholder="Type your own next action" className="w-full border border-[#E2E2E2] rounded-lg px-2 py-1.5 text-sm focus:border-[#FF4802] outline-none" />
        )}
        <div className="flex gap-2 text-xs items-center flex-wrap">
          {custom && <button onClick={() => save(draft.trim())} disabled={saving} data-testid={`${testid}-save`} className="flex items-center gap-1 text-[#159A50] font-medium"><Check className="w-3.5 h-3.5" />{saving ? "Saving…" : "Save"}</button>}
          <button onClick={() => { setEditing(false); setCustom(false); }} className="flex items-center gap-1 text-[#6B7280]"><X className="w-3.5 h-3.5" /> Cancel</button>
          {!lastConvIsToday && (
            <label className="ml-auto flex items-center gap-1.5 text-[#6B7280] cursor-pointer" data-testid={`${testid}-spoke-label`}>
              <input type="checkbox" checked={spoke} onChange={(e) => setSpoke(e.target.checked)} data-testid={`${testid}-spoke-toggle`} className="accent-[#FF4802]" />
              Spoke to the customer today? <span className="text-[#C5C4C4]">(sets Last Conversation)</span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
